// =====================================================
// COOLER ENGINE (GENERIC GAS-WATER COOLER)
// =====================================================

// =====================================================
// INTERNAL MEMORY STORAGE
// Keeps realistic lag response
// =====================================================

const coolerMemory = {};

// =====================================================
// HELPER
// =====================================================

function smoothValue(tag, target, rate = 0.05) {

  if (coolerMemory[tag] === undefined) {
    coolerMemory[tag] = target;
  }

  coolerMemory[tag] +=
    (target - coolerMemory[tag]) * rate;

  return coolerMemory[tag];
}

// =====================================================
// MAIN COOLER SIMULATION
// =====================================================

export function simulateCooler({

  // ================= GAS =================

  gasFlow = 0,
  gasTempIn = 0,
  gasPressureIn = 0,

  // ================= WATER =================

  waterFlow = 0,
  waterTempIn = 0,
  waterPressureIn = 0,

  // ================= VALVES =================

  gasValveOpen = false,
  waterValveOpen = false,

  // ================= CONFIG =================

  coolerId = "DEFAULT",

  designEfficiency = 0.82,

  foulingFactor = 0.0,

  pressureDropFactor = 0.015,

  thermalResponseRate = 0.05

}) {

  // =====================================================
  // BASIC STATUS
  // =====================================================

  const gasAvailable =
    gasValveOpen &&
    gasFlow > 0;

  const waterAvailable =
    waterValveOpen &&
    waterFlow > 0;

  // =====================================================
  // DEFAULT RETURNS
  // =====================================================

  let gasTempOut =
    gasTempIn;

  let gasPressureOut =
    gasPressureIn;

  let waterTempOut =
    waterTempIn;

  let deltaP = 0;

  let heatTransfer = 0;

  let coolingEfficiency = 0;

  let status = "IDLE";

  // =====================================================
  // NO GAS
  // =====================================================

  if (!gasAvailable) {

    status = "NO_GAS";

    return {

      gasTempOut,
      gasPressureOut,
      waterTempOut,
      deltaP,
      heatTransfer,
      coolingEfficiency,
      status
    };
  }

  // =====================================================
  // GAS BUT NO WATER
  // =====================================================

  if (gasAvailable && !waterAvailable) {

    status = "NO_WATER";

    gasTempOut =
      smoothValue(
        `${coolerId}_gasTempOut`,
        gasTempIn - 1,
        thermalResponseRate
      );

    gasPressureOut =
      gasPressureIn - 0.01;

    return {

      gasTempOut,
      gasPressureOut,
      waterTempOut,
      deltaP,
      heatTransfer,
      coolingEfficiency,
      status
    };
  }

  // =====================================================
  // COOLING CALCULATIONS
  // =====================================================

  status = "RUNNING";

  // =========================================
  // EFFECTIVENESS
  // =========================================

  coolingEfficiency =

    designEfficiency *

    (1 - foulingFactor);

  // =========================================
  // TEMPERATURE DIFFERENCE
  // =========================================

  const deltaT =

    gasTempIn -
    waterTempIn;

  // =========================================
  // FLOW EFFECT
  // =========================================

  const flowFactor =

    Math.min(
      waterFlow / (gasFlow + 1),
      1.5
    );

  // =========================================
  // HEAT TRANSFER
  // =========================================

  heatTransfer =

    deltaT *
    coolingEfficiency *
    flowFactor;

  // =========================================
  // TARGET GAS OUT TEMP
  // =========================================

  const targetGasTempOut =

    gasTempIn -
    heatTransfer;

  // =========================================
  // TARGET WATER TEMP OUT
  // =========================================

  const targetWaterTempOut =

    waterTempIn +
    (heatTransfer * 0.65);

  // =========================================
  // PRESSURE DROP
  // =========================================

  deltaP =

    gasFlow *
    pressureDropFactor /
    100;

  const targetGasPressureOut =

    Math.max(
      gasPressureIn - deltaP,
      0
    );

  // =====================================================
  // APPLY DYNAMIC RESPONSE LAG
  // =====================================================

  gasTempOut =
    smoothValue(
      `${coolerId}_gasTempOut`,
      targetGasTempOut,
      thermalResponseRate
    );

  waterTempOut =
    smoothValue(
      `${coolerId}_waterTempOut`,
      targetWaterTempOut,
      thermalResponseRate
    );

  gasPressureOut =
    smoothValue(
      `${coolerId}_gasPressureOut`,
      targetGasPressureOut,
      0.08
    );

  // =====================================================
  // RETURN
  // =====================================================

  return {

    gasTempOut,
    gasPressureOut,
    waterTempOut,

    deltaP,

    heatTransfer,

    coolingEfficiency,

    status
  };
}